import React from "react";
import Markdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { EtfBreadcrumb } from "../components/layout/EtfBreadcrumb";
import { EtfNewsletter } from "../components/layout/EtfNewsletter";
import { RelatedPosts } from "../components/legal/RelatedPosts";
import type { Fund } from "../data/funds";
import { type BlogPostView, useBlogPost, useBlogPosts } from "../hooks/use-blog-posts";
import { useFundBySlug } from "../hooks/use-fund-list";
import { fundDetailPath } from "../lib/fund-paths";
import { JsonLd, Seo, blogPostingLd, breadcrumbLd } from "../lib/seo";

// BlogPostPage — single blog post.
// Ported from dane/frontend/app/etf-website/blog/post/page.tsx. Now backed by
// the public API (`useBlogPost` → `/api/data/posts/:slug`), which returns only
// published + env-live posts (404 otherwise → inline not-found fallback).
// Layout: breadcrumb → 1+sidebar grid (1fr_320px). Left column has the
// header (category eyebrow + title + excerpt + author/date/read-time strip)
// and the markdown body. Right column has a sticky TOC ("In This Article")
// and an optional Related Fund card. Below: RelatedPosts + EtfNewsletter.

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function readTimeLabel(minutes: number): string {
  return `${minutes} min read`;
}

// Build a stable slug from a heading title. Mirrors the source page's
// `extractTocItems` so the markdown H2 renderer and the TOC anchors agree.
function slugifyHeading(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 40);
}

// Walk the React children tree and concatenate string/number leaves. The
// markdown H2 renderer receives ReactNode children (e.g. `[text, <em>X</em>]`
// for "## Foo **bar**"), and `String(children)` on an array yields
// "[object Object]" which mangles the anchor id. Recursing through children
// returns the same plain text the TOC built from the raw markdown source.
function extractText(children: React.ReactNode): string {
  return React.Children.toArray(children)
    .map((c) => {
      if (typeof c === "string") return c;
      if (typeof c === "number") return String(c);
      if (React.isValidElement(c)) {
        const props = c.props as { children?: React.ReactNode };
        if (typeof props.children !== "undefined") {
          return extractText(props.children);
        }
      }
      return "";
    })
    .join("");
}

function extractTocItems(markdown: string): { label: string; id: string }[] {
  return markdown
    .split("\n")
    .filter((line) => line.startsWith("## "))
    .map((line) => {
      const label = line.replace(/^## /, "").trim();
      return { label, id: slugifyHeading(label) };
    });
}

export function BlogPostPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { data: post, isLoading } = useBlogPost(slug);

  if (isLoading) return <PostLoading />;
  // `null` (404 / not published / not env-live) and `undefined` (no slug) both
  // render the not-found fallback rather than crashing.
  if (!post) return <PostNotFound slug={slug} />;

  return <BlogPostArticle post={post} />;
}

// ─── Sub-components ──────────────────────────────────────────────────

function BlogPostArticle({ post }: { post: BlogPostView }) {
  const tocItems = extractTocItems(post.contentMarkdown);
  // Look up the related fund from the DDB list by slug (= fund.id).
  const { data: relatedFund } = useFundBySlug(post.relatedFundSlug ?? null);
  // Pool for "More From Corgi Insights" — reuses the cached ["posts"] query.
  const { data: relatedPool } = useBlogPosts();

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt || `${post.title} — Corgi Funds Insights.`}
        canonicalPath={`/blog/${post.slug}`}
        type="article"
      />
      <JsonLd
        data={[
          blogPostingLd({
            slug: post.slug,
            title: post.title,
            excerpt: post.excerpt,
            author: post.author,
            category: post.category,
            publishedAt: post.publishedAt,
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/blog" },
            { name: post.title },
          ]),
        ]}
      />
      <EtfBreadcrumb
        items={[
          { label: "Insights", href: "/blog" },
          { label: post.category, href: "/blog" },
          { label: post.title },
        ]}
        separator={" / "}
      />

      <div className="max-w-[1200px] mx-auto px-10 max-md:px-5 pb-20 max-md:pb-[60px] grid grid-cols-[1fr_320px] max-[960px]:grid-cols-1 gap-20 max-[960px]:gap-10 items-start">
        <div className="min-w-0">
          <BlogPostHeader post={post} />
          <BlogPostBody markdown={post.contentMarkdown} />
        </div>

        <BlogPostSidebar tocItems={tocItems} relatedFund={relatedFund} />
      </div>

      <RelatedPosts currentSlug={post.slug} pool={relatedPool ?? []} />
      <EtfNewsletter />
    </>
  );
}

function BlogPostHeader({ post }: { post: BlogPostView }) {
  return (
    <div className="pt-16">
      <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.16em] uppercase text-[var(--etf-orange)] mb-5 flex items-center gap-3 before:content-[''] before:w-6 before:h-px before:bg-[var(--etf-orange)] before:inline-block">
        {post.category}
      </div>
      <h1 className="font-[family-name:var(--font-f37bolton)] text-[clamp(40px,5vw,72px)] font-normal leading-none tracking-[-0.02em] mb-6 text-[var(--etf-white)]">
        {post.title}
      </h1>
      <p className="text-[19px] italic font-light text-[var(--etf-white2)] leading-[1.65] mb-8">
        {post.excerpt}
      </p>
      <BlogPostBylineStrip post={post} />
    </div>
  );
}

function BlogPostBylineStrip({ post }: { post: BlogPostView }) {
  return (
    <div className="flex max-md:flex-col items-center max-md:items-start gap-5 max-md:gap-3 py-5 border-t border-b border-[var(--etf-border2)] mb-0">
      <div className="flex items-center gap-2.5 flex-1">
        <div className="w-9 h-9 rounded-full bg-[var(--etf-black3)] border border-[var(--etf-border)] flex items-center justify-center font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] shrink-0">
          {post.authorInitials}
        </div>
        <div>
          <div className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white)]">
            {post.author}
          </div>
          <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] mt-px">
            {post.authorTitle} · Corgi Funds
          </div>
        </div>
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.06em]">
        {formatDate(post.publishedAt)}
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.06em]">
        {readTimeLabel(post.readTimeMinutes)}
      </div>
    </div>
  );
}

function BlogPostBody({ markdown }: { markdown: string }) {
  return (
    <div className="pt-12">
      <EtfMarkdownContent markdown={markdown} />
    </div>
  );
}

function BlogPostSidebar({
  tocItems,
  relatedFund,
}: {
  tocItems: { label: string; id: string }[];
  // DDB Fund (null = fund not found in DDB after query completes)
  relatedFund: Fund | null | undefined;
}) {
  return (
    <div className="pt-16 sticky top-3 max-[960px]:static max-[960px]:pt-0">
      {tocItems.length > 0 && <TocCard items={tocItems} />}
      {relatedFund && <RelatedFundCard fund={relatedFund} />}
    </div>
  );
}

function TocCard({ items }: { items: { label: string; id: string }[] }) {
  return (
    <div className="border border-[var(--etf-border)] p-5 mb-5">
      <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.16em] uppercase text-[var(--etf-white3)] mb-3.5 pb-2.5 border-b border-[var(--etf-border2)]">
        In This Article
      </div>
      {items.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          className="block font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white3)] no-underline py-1.5 pl-2.5 border-l-2 border-transparent transition-all duration-150 tracking-[0.02em] hover:text-[var(--etf-white2)] hover:border-l-[var(--etf-border)]"
        >
          {item.label}
        </a>
      ))}
    </div>
  );
}

function RelatedFundCard({ fund }: { fund: Fund }) {
  return (
    <div className="bg-[var(--etf-black2)] border border-[var(--etf-border)] border-l-[3px] border-l-[var(--etf-orange)] p-5">
      <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.14em] uppercase text-[var(--etf-orange)] mb-2.5">
        Related Fund
      </div>
      <div className="font-[family-name:var(--font-f37bolton)] text-[28px] text-[var(--etf-white)] leading-none mb-1 tracking-[-0.02em]">
        {fund.ticker}
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] mb-3.5">
        {fund.fullName ?? fund.name}
      </div>
      {/* NAV is live data — not stored on the DDB record */}
      <RelatedFundRow label="NAV" value="—" />
      <RelatedFundRow label="Exp. Ratio" value={fund.expenseRatio} />
      <RelatedFundRow label="AUM" value={fund.aum ?? "—"} hideBorder />
      <Link
        to={fundDetailPath(fund.id)}
        className="block w-full mt-3.5 bg-[var(--etf-orange)] text-white border-none py-[11px] font-[family-name:var(--font-instrument)] text-sm tracking-[0.08em] uppercase cursor-pointer text-center no-underline transition-opacity duration-150 hover:opacity-[0.88]"
      >
        View Full Fund Page →
      </Link>
    </div>
  );
}

function RelatedFundRow({
  label,
  value,
  hideBorder,
}: {
  label: string;
  value: string;
  hideBorder?: boolean;
}) {
  const borderClass = hideBorder ? "" : "border-b border-[var(--etf-border2)]";
  return (
    <div
      className={`flex justify-between py-2 font-[family-name:var(--font-instrument)] text-sm ${borderClass}`}
    >
      <span className="text-[var(--etf-white3)]">{label}</span>
      <span className="text-[var(--etf-white)]">{value}</span>
    </div>
  );
}

function PostLoading() {
  return (
    <div className="max-w-[700px] mx-auto px-10 py-24 text-center">
      <div className="font-[family-name:var(--font-instrument)] text-sm tracking-[0.06em] text-[var(--etf-white3)]">
        Loading article…
      </div>
    </div>
  );
}

function PostNotFound({ slug }: { slug: string }) {
  return (
    <div className="max-w-[700px] mx-auto px-10 py-24 text-center">
      <Seo title="Post Not Found" description="This blog post could not be found." noindex />
      <div className="font-[family-name:var(--font-instrument)] text-xs uppercase tracking-[0.16em] text-[var(--etf-orange)] mb-3">
        Post not found
      </div>
      <h1 className="font-[family-name:var(--font-f37bolton)] text-[clamp(40px,5vw,60px)] font-normal leading-none tracking-[-0.02em] mb-4 text-[var(--etf-white)]">
        We couldn't find “{slug}”.
      </h1>
      <p className="text-[15px] font-light italic text-[var(--etf-white2)] leading-[1.7] mb-8">
        The blog post you're looking for may have moved or been retired.
      </p>
      <Link
        to="/blog"
        className="inline-flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] no-underline tracking-[0.06em] transition-[gap] duration-200 hover:gap-2.5"
      >
        Back to Blog →
      </Link>
    </div>
  );
}

// ─── Markdown renderer ───────────────────────────────────────────────
// Components map mirrors dane's blog/post/page.tsx EtfMarkdownContent.
// react-markdown 9 typings allow named components per element so we keep
// each renderer typed as JSX with destructured `children` / `href`.

function EtfMarkdownContent({ markdown }: { markdown: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      disallowedElements={["script", "iframe", "object", "embed", "form"]}
      unwrapDisallowed
      components={{
        h2: ({ children }) => {
          const text = extractText(children);
          return (
            <h2
              id={slugifyHeading(text)}
              className="font-[family-name:var(--font-f37bolton)] text-[32px] tracking-[-0.02em] font-normal text-[var(--etf-white)] mt-12 mb-4 pb-2.5 border-b border-[var(--etf-border2)] leading-none first:mt-0"
            >
              {children}
            </h2>
          );
        },
        h3: ({ children }) => (
          <h3 className="text-2xl font-semibold text-[var(--etf-white)] mt-9 mb-3 leading-[1.15]">
            {children}
          </h3>
        ),
        p: ({ children }) => (
          <p className="text-[17px] leading-[1.78] text-[var(--etf-white2)] mb-5 font-light">
            {children}
          </p>
        ),
        ul: ({ children }) => (
          <ul className="ml-6 mb-5 text-[17px] leading-[1.78] text-[var(--etf-white2)] font-light [&>li]:mb-2">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="ml-6 mb-5 list-decimal text-[17px] leading-[1.78] text-[var(--etf-white2)] font-light [&>li]:mb-2">
            {children}
          </ol>
        ),
        li: ({ children }) => <li>{children}</li>,
        strong: ({ children }) => (
          <strong className="text-[var(--etf-white)] font-medium not-italic">{children}</strong>
        ),
        em: ({ children }) => <em className="text-[var(--etf-white)] italic">{children}</em>,
        blockquote: ({ children }) => (
          <div className="border-l-[3px] border-[var(--etf-orange)] py-5 px-7 my-9 bg-[var(--etf-black2)]">
            <div className="text-xl italic text-[var(--etf-white)] leading-[1.55] m-0">
              {children}
            </div>
          </div>
        ),
        a: ({ children, href }) => (
          <a
            href={href}
            className="text-[var(--etf-orange)] underline underline-offset-2 hover:opacity-80"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),
        hr: () => <hr className="border-[var(--etf-border2)] my-10" />,
        table: ({ children }) => (
          <div className="my-7 overflow-x-auto">
            <table className="w-full border-collapse text-[15px] text-[var(--etf-white2)] font-light">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="border-b border-[var(--etf-border2)] text-[var(--etf-white)]">
            {children}
          </thead>
        ),
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => (
          <tr className="border-b border-[var(--etf-border2)] last:border-b-0">{children}</tr>
        ),
        th: ({ children }) => (
          <th className="py-3 pr-5 text-left font-medium align-top">{children}</th>
        ),
        td: ({ children }) => <td className="py-3 pr-5 align-top leading-[1.6]">{children}</td>,
      }}
    >
      {markdown}
    </Markdown>
  );
}
